import { z } from 'zod';

import { jsonRequest } from './shared';

export type HandheldDeviceStatus = 'active' | 'pending' | 'revoked';

export interface HandheldDeviceApiRecord {
  id: string;
  name: string;
  status: HandheldDeviceStatus;
  deviceStatus: string | null;
  locationId: string | null;
  locationName: string | null;
  lastSeenAt: string | null;
  createdAt: string | null;
  tokenLastFour: string | null;
  tokenRotatedAt: string | null;
}

interface ListHandheldDevicesResponse {
  ok: boolean;
  devices: HandheldDeviceApiRecord[];
}

export interface ProvisionHandheldDevicePayload {
  name: string;
  locationId?: string;
}

interface ProvisionHandheldDeviceResponse {
  ok: boolean;
  device: unknown;
  token: string;
}

export interface RotateHandheldDevicePayload {
  label?: string;
}

interface RotateHandheldDeviceResponse {
  ok: boolean;
  token: string;
}

interface RevokeHandheldDeviceResponse {
  ok: boolean;
}

const HandheldDeviceSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    status: z.string(),
    deviceStatus: z.string().nullable(),
    locationId: z.string().nullable(),
    locationName: z.string().nullable(),
    lastSeenAt: z.string().nullable(),
    createdAt: z.string().nullable(),
    tokenLastFour: z.string().nullable(),
    tokenRotatedAt: z.string().nullable(),
  })
  .passthrough();

const ListHandheldDevicesResponseSchema = z
  .object({
    ok: z.boolean(),
    devices: z.array(HandheldDeviceSchema),
  })
  .passthrough();

const ProvisionHandheldDeviceResponseSchema = z
  .object({
    ok: z.boolean(),
    device: z.unknown(),
    token: z.string(),
  })
  .passthrough();

const RotateHandheldDeviceResponseSchema = z
  .object({
    ok: z.boolean(),
    token: z.string(),
  })
  .passthrough();

const RevokeHandheldDeviceResponseSchema = z
  .object({
    ok: z.boolean(),
  })
  .passthrough();

export async function listHandheldDevices(params: { status?: HandheldDeviceStatus } = {}): Promise<HandheldDeviceApiRecord[]> {
  const search = new URLSearchParams();
  if (params.status) {
    search.set('status', params.status);
  }
  const payload = await jsonRequest<ListHandheldDevicesResponse>(
    `/handheld/devices${search.toString() ? `?${search.toString()}` : ''}`,
    {
      headers: { Accept: 'application/json' },
      schema: ListHandheldDevicesResponseSchema,
    },
  );
  return payload.devices ?? [];
}

export async function provisionHandheldDevice(payload: ProvisionHandheldDevicePayload) {
  return jsonRequest<ProvisionHandheldDeviceResponse>('/handheld/devices', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: payload,
    schema: ProvisionHandheldDeviceResponseSchema,
  });
}

export async function rotateHandheldDevice(deviceId: string, payload: RotateHandheldDevicePayload = {}) {
  return jsonRequest<RotateHandheldDeviceResponse>(`/handheld/devices/${deviceId}/rotate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: payload,
    schema: RotateHandheldDeviceResponseSchema,
  });
}

export async function revokeHandheldDevice(deviceId: string) {
  return jsonRequest<RevokeHandheldDeviceResponse>(`/handheld/devices/${deviceId}/revoke`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    schema: RevokeHandheldDeviceResponseSchema,
  });
}
